import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowUpRight, CheckCircle2, Clock } from 'lucide-react'
import publications from '@/data/publications'
import { profile } from '@/data/site'
import { SiteShell } from '@/components/layout/SiteShell'
import { AuthorList } from '@/components/sections/Publications/AuthorList'
import { applyPageMeta } from '@/seo/head'
import { publicationSeo } from '@/seo/publication-seo'
import { SEO } from '@/seo/seo-config'

const ease = [0.22, 1, 0.36, 1] as const

export const PublicationPage = () => {
  const { id = '' } = useParams<{ id: string }>()
  const pub = publications.find((p) => p.id === id)
  const seo = publicationSeo(id)

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    if (seo) {
      applyPageMeta({ title: seo.title, description: seo.description, canonical: seo.canonical, type: 'article' })
    } else {
      applyPageMeta({
        title: `Publication not found | ${SEO.name}`,
        description: 'No publication matches that address.',
        canonical: `${SEO.portfolioURL}/publications/`,
        robots: 'noindex, follow',
      })
    }
  }, [id, seo])

  if (!pub) {
    return (
      <SiteShell>
        <section className="container flex min-h-[70vh] flex-col items-start justify-center pt-32">
          <p className="eyebrow mb-4">404</p>
          <h1 className="text-4xl font-medium tracking-tight text-white">Publication not found</h1>
          <p className="mt-4 text-zinc-400">No publication matches that address.</p>
          <Link to="/#publications" className="link-underline mt-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> All publications
          </Link>
        </section>
      </SiteShell>
    )
  }

  const published = pub.status === 'Published'

  return (
    <SiteShell>
      {seo && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(seo.jsonLd) }} />
      )}

      <article className="container max-w-3xl pb-24 pt-32 md:pt-40">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <nav aria-label="Breadcrumb" className="mb-10">
            <ol className="flex flex-wrap items-center gap-2 font-mono text-xs text-zinc-500">
              <li>
                <Link to="/" className="hover:text-zinc-200">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/#publications" className="hover:text-zinc-200">
                  Publications
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-zinc-400" aria-current="page">
                {pub.id}
              </li>
            </ol>
          </nav>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span
              className={
                published
                  ? 'inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 font-medium text-emerald-300'
                  : 'inline-flex items-center gap-1.5 rounded-full border border-amber-300/20 bg-amber-300/10 px-2.5 py-1 font-medium text-amber-200'
              }
            >
              {published ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
              {published ? 'Published' : 'Accepted · in press'}
            </span>
            <span className="font-mono text-zinc-500">{pub.year}</span>
          </div>

          <h1 className="mt-6 text-balance text-3xl font-medium leading-tight tracking-tight text-white md:text-5xl">
            {pub.title}
          </h1>
          <p className="mt-5 text-zinc-400">{pub.conference}</p>
          <AuthorList authors={pub.authors} highlight={profile.name} className="mt-3" />

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {pub.publisherLink && (
              <a
                href={pub.publisherLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-zinc-50 px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-white"
              >
                Read on IEEE Xplore <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
            {pub.doi && <span className="font-mono text-xs text-zinc-500">DOI {pub.doi}</span>}
          </div>

          {pub.metrics && (
            <dl className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {pub.metrics.map((m) => (
                <div key={m.label} className="glass rounded-2xl px-5 py-4">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{m.label}</dt>
                  <dd className="mt-1 text-2xl font-medium text-white">{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </motion.div>

        <div className="mt-16 space-y-14">
          {pub.sections.map((section) => (
            <motion.section
              key={section.heading}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease }}
            >
              <h2 className="eyebrow mb-5 flex items-center gap-3">
                <span className="h-px w-8 bg-gradient-to-r from-blush to-lilac" aria-hidden="true" />
                {section.heading}
              </h2>
              {section.content && (
                <p className="text-pretty text-[17px] leading-[1.8] text-zinc-300">{section.content}</p>
              )}
              {section.figures && section.figures.length > 0 && (
                <div className="grid gap-6">
                  {section.figures.map((fig) => (
                    <figure key={fig.src} className="glass overflow-hidden rounded-2xl">
                      <div className="bg-white p-4">
                        <img
                          src={fig.src}
                          alt={fig.caption}
                          loading="lazy"
                          decoding="async"
                          className="mx-auto h-auto max-w-full"
                        />
                      </div>
                      <figcaption className="px-5 py-4 text-sm leading-relaxed text-zinc-400">{fig.caption}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </motion.section>
          ))}
        </div>

        <div className="mt-20 flex flex-wrap items-center justify-between gap-4 border-t border-white/[0.06] pt-8">
          <Link to="/#publications" className="link-underline text-sm">
            <ArrowLeft className="h-4 w-4" /> All publications
          </Link>
          <Link to="/#contact" className="link-underline text-sm">
            Discuss this paper <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </article>
    </SiteShell>
  )
}

export default PublicationPage
